"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";

import { DASHBOARD_ROUTES } from "@/dashboard/constants/routes";
import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import { ALL_TENANTS_ID } from "@/dashboard/lib/auth/constants";

/**
 * Switch active tenant (client-side)
 * – POST /api/dashboard/tenant/select
 */
export function useSelectTenant(setActiveTenantId: (id: string) => void) {
  const qc = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();

  // checks if the path is /dashboard/hub/[customerId]
  const isCustomerDetail = pathname && /^\/dashboard\/hub\/[^/]+$/.test(pathname);
  // checks if we're viewing a campaign details page: /dashboard/marketing/campaigns/[campaignId]
  const isCampaignDetailsPage = pathname
    ? /^\/dashboard\/marketing\/campaigns\/[^/]+$/.test(pathname)
    : false;
  // checks if we're on the campaign builder page
  const isCampaignBuilderPage = pathname
    ? pathname.startsWith(DASHBOARD_ROUTES.MARKETING_CAMPAIGNS_BUILDER)
    : false;

  // Call scoring dynamic routes - /dashboard/call-scoring/review/[id] or /dashboard/call-scoring/user/[id]
  const isCallScoringReviewDetail = pathname
    ? /^\/dashboard\/call-scoring\/review\/[^/]+$/.test(pathname)
    : false;
  const isCallScoringUserDetail = pathname
    ? /^\/dashboard\/call-scoring\/user\/[^/]+$/.test(pathname)
    : false;

  // Call rubric groups dynamic routes - any route with a groupId parameter
  // Matches: /dashboard/call-rubric-groups/[groupId], /dashboard/call-rubric-groups/[groupId]/rubrics/[rubricId], etc.
  const isCallRubricGroupDetail = pathname
    ? /^\/dashboard\/call-rubric-groups\/[^/]+/.test(pathname)
    : false;

  return useMutation({
    mutationFn: (tenantId: string) =>
      fetchJson<{ success: true }>("/api/dashboard/tenant/select", {
        method: "POST",
        body: JSON.stringify({ tid: tenantId }),
      }).then(() => tenantId),
    onSuccess: (newTenantId: string) => {
      // ensure server-authoritative state is fetched afterwards
      void qc.invalidateQueries({ queryKey: ["me"] });

      // Clear all tenant-scoped caches so UI doesn't show stale data between tenants
      void qc.removeQueries({ queryKey: ["tenant"] });

      if (newTenantId !== ALL_TENANTS_ID) {
        if (isCustomerDetail) {
          // go to Hub root but tell it not to auto‑select
          router.replace(`${DASHBOARD_ROUTES.HUB}?skipAutoSelect=1`);
        } else if (isCampaignDetailsPage || isCampaignBuilderPage) {
          // When switching tenants from a campaign details or builder page,
          // navigate back to the campaigns list to avoid stale tenant-scoped state
          router.replace(DASHBOARD_ROUTES.MARKETING_CAMPAIGNS);
        } else if (isCallScoringReviewDetail) {
          // When switching tenants from a call scoring review detail page,
          // navigate back to the call scoring home to avoid showing invalid records
          router.replace(DASHBOARD_ROUTES.CALL_SCORING);
        } else if (isCallScoringUserDetail) {
          // When switching tenants from a call scoring user detail page,
          // navigate back to the call scoring home to avoid showing invalid records
          router.replace(DASHBOARD_ROUTES.CALL_SCORING);
        } else if (isCallRubricGroupDetail) {
          // When switching tenants from any call rubric group detail page,
          // navigate back to the rubric groups list to avoid showing invalid records
          router.replace(DASHBOARD_ROUTES.CALL_RUBRIC_GROUPS);
        } else {
          router.refresh();
        }
      }

      setActiveTenantId(newTenantId);
    },
  });
}
